import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import Alert, { ALERT_TYPES } from '../../components/atoms/Alert';
import { getPlatformIcon } from '../../utils/OS';
import AddFutureItems from '../containers/AddFutureItems';
import { getTranslatedItemType, translationPrefix } from '../utils';
import css from './AppPageStep.css';
import SearchableMultiSelect from './SearchableMultiSelect';
import StepCard from './StepCard';

const getFormattedSite = site => {
    const formattedPlatform = `${site.platform.charAt(0).toUpperCase()}${site.platform.slice(1)}`;
    return {
        ...site,
        id: site.publicId,
        displayName: `${formattedPlatform} - ${site.publicId}`,
        Icon: getPlatformIcon(site.platform),
    };
};

const AppPageStep = ({
    apps = [],
    dimensionType = 'app',
    onChange = () => {},
    enableAddFuture = () => {},
    onStepValidationStatusChanged = () => {},
    translate = () => {},
    removedAppSites = [],
    activeMemberType = '',
}) => {
    const isAppLevel = dimensionType === 'app';
    const itemType = isAppLevel ? 'apps' : 'appsites';
    const selectedAppsCount = apps.filter(app => app.selected || app.sites?.some(site => site.selected)).length;
    const selectedCount = isAppLevel
        ? selectedAppsCount
        : apps.flatMap(app => app.sites.filter(site => site.selected)).length;
    const totalSitesCount = isAppLevel ? 0 : apps.flatMap(app => app.sites || []).length;
    const disabledAppsCount = apps.filter(app => app.disabled).length;
    const totalAvailableAppsCount = apps.length;
    const allAppsSelected = selectedAppsCount === totalAvailableAppsCount - disabledAppsCount && selectedAppsCount > 0;
    const highlightInfo = !isAppLevel && allAppsSelected && selectedCount < totalSitesCount;

    const allSelectableIds = useMemo(
        () =>
            isAppLevel
                ? apps.filter(app => !app.disabled).map(app => app.id)
                : apps
                      .filter(app => !app.disabled)
                      .flatMap(app => app.sites.filter(site => !site.disabled).map(site => site.publicId)),
        [apps, isAppLevel]
    );

    const formattedApps = useMemo(
        () =>
            apps.map(app => ({
                ...app,
                ...(isAppLevel ? {} : { subItems: app.sites.map(getFormattedSite) }),
                ...(app.icon && {
                    Icon: 'img',
                    iconProps: { src: app.icon, alt: app.name },
                }),
            })),
        [apps, isAppLevel]
    );

    useEffect(() => {
        const isValid = selectedCount > 0;
        onStepValidationStatusChanged({
            isValid,
            title: isValid
                ? ''
                : translate('STATIC.PAGES.TEAM_MEMBERS.SELECT_AT_LEAST_ONE_TOOLTIP', {
                      itemType: getTranslatedItemType(translate, itemType),
                  }),
        });
    }, [onStepValidationStatusChanged, translate, selectedCount, itemType]);

    const selectAll = value => {
        onChange(allSelectableIds, value);
        if (value) {
            enableAddFuture();
        }
    };

    const onItemSelected = (selectedId, value, isSubItem = false) => {
        if (isAppLevel || isSubItem) {
            onChange([selectedId], value);
        } else {
            const app = apps.find(({ id }) => id === selectedId);
            if (app) {
                onChange(
                    app.sites.map(site => site.publicId),
                    value
                );
            }
        }
    };

    return (
        <div className={css.container}>
            {removedAppSites.length > 0 && (
                <Alert
                    type={ALERT_TYPES.WARNING}
                    text={translate(`${translationPrefix}.APP_REMOVAL_WARNING`)}
                    className={css.alert}
                    alertIconClassName={css.alertIcon}
                />
            )}
            <StepCard
                className={css.stepCard}
                title={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.TITLE.APPS`}
                titleTextReplace={{
                    count: selectedAppsCount,
                    total: totalAvailableAppsCount - disabledAppsCount,
                }}
                info={
                    !isAppLevel
                        ? translate('STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.ITEM_SELECTED', {
                              selected: selectedCount,
                              total: totalSitesCount,
                              itemType: getTranslatedItemType(translate, itemType),
                          })
                        : ''
                }
                highlightInfo={highlightInfo}
                description={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.DESCRIPTION.${activeMemberType}.APPS`}
            >
                <SearchableMultiSelect
                    className={css.multiSelectContainer}
                    allItems={formattedApps}
                    filteredItems={formattedApps}
                    onChange={onItemSelected}
                    selectAll={selectAll}
                    itemType={itemType}
                    searchPlaceholder={translate(`${translationPrefix}.PLACEHOLDER.${itemType.toUpperCase()}_SEARCH`)}
                    sorted={false}
                />
            </StepCard>
            <AddFutureItems itemType={itemType} form={dimensionType} dimensionFilter className={css.addFutureItems} />
        </div>
    );
};

AppPageStep.propTypes = {
    apps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string.isRequired,
            icon: PropTypes.string.isRequired,
            selected: PropTypes.bool.isRequired,
            sites: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    publicId: PropTypes.string.isRequired,
                    platform: PropTypes.string.isRequired,
                    icon: PropTypes.string.isRequired,
                })
            ),
        })
    ),
    dimensionType: PropTypes.string,
    onChange: PropTypes.func,
    enableAddFuture: PropTypes.func,
    onStepValidationStatusChanged: PropTypes.func,
    translate: PropTypes.func,
    removedAppSites: PropTypes.arrayOf(PropTypes.string),
    activeMemberType: PropTypes.string,
};

export default AppPageStep;
